import { z } from 'zod';
import { ENTRY_KINDS } from '../entry-kinds.js';
import { MEDIA_TOOLS } from './media-tools.js';
import { id, ifMatch } from './tool-base.js';
/** List tools return at most this many items per call; the client continues with `cursor` (spec 130 section 5.3). */
export const EXTERNAL_TOOL_PAGE_SIZE = 20;
const date = z.iso.date();
const page = {
    limit: z.number().int().min(1).max(EXTERNAL_TOOL_PAGE_SIZE).default(EXTERNAL_TOOL_PAGE_SIZE).describe(`Items per page, at most ${EXTERNAL_TOOL_PAGE_SIZE}.`),
    cursor: z.string().max(500).optional().describe('`next_cursor` of the previous page.'),
};
const search = z.string().max(200);
const READ_TOOLS = [
    {
        name: 'list_properties',
        title: 'List properties',
        description: 'The businesses (properties) of this tenant. Start here: most other tools need a property_id.',
        operationId: 'listProperties',
        scope: 'read',
        method: 'GET',
        path: '/v1/properties',
        input: z.object({ ...page }),
    },
    {
        name: 'get_property',
        title: 'Get a property',
        description: 'Name, address, languages, time zone and profile of one property.',
        operationId: 'getProperty',
        scope: 'read',
        method: 'GET',
        path: '/v1/properties/{property_id}',
        input: z.object({ property_id: id }),
    },
    {
        name: 'get_writing_style',
        title: 'Get the writing style',
        description: 'How posts of this property are written: voice, tone, forbidden phrases, call to action and hashtags. Follow it when writing copy.',
        operationId: 'getPropertyWritingStyle',
        scope: 'read',
        method: 'GET',
        path: '/v1/properties/{property_id}/writing-style',
        input: z.object({ property_id: id }),
    },
    {
        name: 'get_posting_settings',
        title: 'Get the posting settings',
        description: 'Posting rhythm, preferred days and times and enabled formats of a property.',
        operationId: 'getPropertyPostingSettings',
        scope: 'read',
        method: 'GET',
        path: '/v1/properties/{property_id}/posting-settings',
        input: z.object({ property_id: id }),
    },
    {
        name: 'list_media',
        title: 'Search the media library',
        description: 'Photos and videos of a property. Each result carries preview_url, a signed link to the preview image that expires (see url_expires_at); originals are never linked. Use the IDs in drafts.',
        operationId: 'listMediaAssets',
        scope: 'read',
        method: 'GET',
        path: '/v1/media-assets',
        input: z.object({
            property_id: id,
            q: search.optional().describe('Words that must occur in title, description, location or tags.'),
            tag: z.array(z.string().max(100)).max(10).optional().describe('Every one of these tags.'),
            season: z.array(z.enum(['spring', 'summer', 'autumn', 'winter', 'all_year'])).optional(),
            pillar_id: z.array(id).optional(),
            kind: z.array(z.enum(['photo', 'video'])).optional(),
            usable: z.boolean().optional().describe('true: only assets that may be used in a post now.'),
            orientation: z.enum(['portrait', 'square', 'landscape']).optional(),
            entry_id: z.array(id).max(20).optional().describe('Linked to these entries (list_entries): any of one type, every type given.'),
            ...page,
        }),
    },
    {
        name: 'get_media',
        title: 'Get a media asset',
        description: 'One photo or video with tags, season, usability and preview_url.',
        operationId: 'getMediaAsset',
        scope: 'read',
        method: 'GET',
        path: '/v1/media-assets/{media_id}',
        input: z.object({ media_id: id }),
    },
    {
        name: 'list_knowledge_items',
        title: 'Search the knowledge base',
        description: 'Facts about the business. Only items with verified: true may be stated as facts in a post; treat the others as unconfirmed.',
        operationId: 'listKnowledgeItems',
        scope: 'read',
        method: 'GET',
        path: '/v1/knowledge-items',
        input: z.object({
            property_id: id,
            q: search.optional().describe('Text in title or body.'),
            status: z.array(z.enum(['draft', 'verified', 'outdated'])).optional(),
            language: z.string().max(35).optional(),
            ...page,
        }),
    },
    {
        name: 'get_knowledge_item',
        title: 'Get a knowledge item',
        description: 'One fact with its full text and verified flag.',
        operationId: 'getKnowledgeItem',
        scope: 'read',
        method: 'GET',
        path: '/v1/knowledge-items/{knowledge_item_id}',
        input: z.object({ knowledge_item_id: id }),
    },
    {
        name: 'list_entry_types',
        title: 'List entry types',
        description: 'The kinds of things the business names (team, apartments, locations, products, ...), each with its entry count.',
        operationId: 'listEntryTypes',
        scope: 'read',
        method: 'GET',
        path: '/v1/entry-types',
        input: z.object({ property_id: id }),
    },
    {
        name: 'list_entries',
        title: 'List entries',
        description: 'People, apartments, locations, products and other named things of a property. Link them to drafts with entry_ids and find their photos with list_media entry_id. Only knowledge items with verified: true may be stated as facts.',
        operationId: 'listEntries',
        scope: 'read',
        method: 'GET',
        path: '/v1/entries',
        input: z.object({
            property_id: id,
            type_id: id.optional(),
            kind: z.array(z.enum(ENTRY_KINDS)).optional(),
            active: z.boolean().optional(),
            q: z.string().max(100).optional().describe('Part of the name or summary.'),
            related_to: id.optional().describe('Only entries related to this entry.'),
        }),
    },
    {
        name: 'get_entry',
        title: 'Get an entry',
        description: 'One entry with its details, relations, linked knowledge items and photo and post counts.',
        operationId: 'getEntry',
        scope: 'read',
        method: 'GET',
        path: '/v1/entries/{entry_id}',
        input: z.object({ entry_id: id }),
    },
    {
        name: 'list_pillars',
        title: 'List content pillars',
        description: 'The recurring topics (content pillars) of a property.',
        operationId: 'listContentPillars',
        scope: 'read',
        method: 'GET',
        path: '/v1/content-pillars',
        input: z.object({ property_id: id, include_archived: z.boolean().optional() }),
    },
    {
        name: 'get_pillar',
        title: 'Get a content pillar',
        description: 'One content pillar with its description.',
        operationId: 'getContentPillar',
        scope: 'read',
        method: 'GET',
        path: '/v1/content-pillars/{pillar_id}',
        input: z.object({ pillar_id: id }),
    },
    {
        name: 'list_campaigns',
        title: 'List campaigns',
        description: 'Campaigns with goal and period; filter by status or by a date range they overlap.',
        operationId: 'listCampaigns',
        scope: 'read',
        method: 'GET',
        path: '/v1/campaigns',
        input: z.object({
            property_id: id.optional(),
            status: z.array(z.enum(['draft', 'active', 'completed', 'archived'])).optional(),
            overlaps_from: date.optional(),
            overlaps_to: date.optional(),
            ...page,
        }),
    },
    {
        name: 'get_campaign',
        title: 'Get a campaign',
        description: 'One campaign with goal, period, pillars and role targets.',
        operationId: 'getCampaign',
        scope: 'read',
        method: 'GET',
        path: '/v1/campaigns/{campaign_id}',
        input: z.object({ campaign_id: id }),
    },
    {
        name: 'list_content_items',
        title: 'List posts and ideas',
        description: 'Ideas, drafts, posts in review and approved posts; filter by status, campaign, pillar, desired date or text.',
        operationId: 'listContentItems',
        scope: 'read',
        method: 'GET',
        path: '/v1/content-items',
        input: z.object({
            property_id: id.optional(),
            status: z.array(z.enum(['idea', 'draft', 'in_review', 'changes_requested', 'approved', 'archived'])).optional(),
            campaign_id: id.optional(),
            pillar_id: id.optional(),
            desired_date_from: date.optional(),
            desired_date_to: date.optional(),
            q: search.optional().describe('Text in the title or a caption.'),
            ...page,
        }),
    },
    {
        name: 'get_content_item',
        title: 'Get a post',
        description: 'One post or idea with its texts per language, formats and media.',
        operationId: 'getContentItem',
        scope: 'read',
        method: 'GET',
        path: '/v1/content-items/{content_item_id}',
        input: z.object({ content_item_id: id }),
    },
    {
        name: 'list_publications',
        title: 'List the calendar',
        description: 'Planned, scheduled and published posts in a time range (UTC instants); the calendar of the tenant.',
        operationId: 'listPublications',
        scope: 'read',
        method: 'GET',
        path: '/v1/publications',
        input: z.object({
            scheduled_at_from: z.iso.datetime({ offset: true }).optional(),
            scheduled_at_to: z.iso.datetime({ offset: true }).optional(),
            status: z.array(z.enum(['planned', 'scheduled', 'publishing', 'published', 'failed', 'cancelled'])).optional(),
            content_item_id: id.optional(),
            campaign_id: id.optional(),
            ...page,
        }),
    },
    {
        name: 'get_publication',
        title: 'Get a publication',
        description: 'One planned or published post on one account at one time.',
        operationId: 'getPublication',
        scope: 'read',
        method: 'GET',
        path: '/v1/publications/{publication_id}',
        input: z.object({ publication_id: id }),
    },
    {
        name: 'list_comments',
        title: 'List review comments',
        description: 'The review notes of a post.',
        operationId: 'listContentComments',
        scope: 'read',
        method: 'GET',
        path: '/v1/content-items/{content_item_id}/comments',
        input: z.object({ content_item_id: id, resolved: z.boolean().optional(), ...page }),
    },
    {
        name: 'get_storyboard',
        title: 'Get the storyboard of a reel',
        description: 'The storyboard or uploaded video of one reel or story format (variant_id from get_content_item), with its id and etag. Answers not-found when the format has none yet.',
        operationId: 'getVariantStoryboard',
        scope: 'read',
        method: 'GET',
        path: '/v1/content-variants/{variant_id}/storyboard',
        input: z.object({ variant_id: id }),
    },
];
const nullableText = (max) => z.string().max(max).nullable();
const campaignFields = {
    goal: nullableText(2000).optional(),
    target_markets: z.array(z.string().length(2)).max(20).optional().describe('ISO 3166-1 alpha-2 country codes in upper case.'),
    languages: z.array(z.string().max(35)).max(10).optional().describe('BCP-47 tags.'),
    pillar_ids: z.array(id).max(20).optional().describe('Content pillars of the same property.'),
    planned_count: z.number().int().min(0).max(500).nullable().optional(),
    cta: nullableText(300).optional(),
    landing_url: nullableText(2000).optional().describe('Absolute https:// URL.'),
};
const itemFields = {
    description: nullableText(5000).optional().describe('Briefing: what the post is about.'),
    pillar_id: id.nullable().optional(),
    campaign_id: id.nullable().optional(),
    campaign_role: z.enum(['opening', 'moment', 'closing']).nullable().optional(),
    desired_date: date.nullable().optional().describe('Proposed publishing day in the tenant time zone; creates no publication.'),
    priority: z.enum(['low', 'normal', 'high']).optional(),
    season: z.enum(['spring', 'summer', 'autumn', 'winter', 'all_year']).nullable().optional(),
    evergreen: z.boolean().optional(),
    entry_ids: z.array(id).max(20).optional().describe('The entries (list_entries) the post is about; replaces the set.'),
};
const textFields = {
    headline: z.string().max(300).optional(),
    caption: z.string().max(10000).optional(),
    hashtags: z.array(z.string().max(100)).max(100).optional().describe('Without or with a leading #.'),
    cta: z.string().max(300).optional(),
};
const format = z.enum(['ig_feed', 'ig_carousel', 'ig_story', 'fb_feed']);
const time = {
    scheduled_at: z.iso.datetime({ offset: true }).optional().describe('UTC instant, minute precision, in the future.'),
    scheduled_at_local: z.string().max(16).optional().describe('Wall time YYYY-MM-DDTHH:mm in the tenant time zone; instead of scheduled_at.'),
};
/** Scope `plan`: create and revise ideas, drafts and campaigns; nothing here approves or publishes (spec 130 section 5.3). */
const PLAN_TOOLS = [
    {
        name: 'create_campaign',
        title: 'Create a campaign',
        description: 'A new campaign as a draft. A human activates it.',
        operationId: 'createCampaign',
        scope: 'plan',
        method: 'POST',
        path: '/v1/campaigns',
        input: z.object({ property_id: id, name: z.string().min(1).max(200), starts_on: date, ends_on: date, ...campaignFields }),
    },
    {
        name: 'update_campaign',
        title: 'Change a campaign',
        description: 'Name, goal, period, pillars and call to action of any campaign; its status cannot change.',
        operationId: 'updateCampaign',
        scope: 'plan',
        method: 'PATCH',
        path: '/v1/campaigns/{campaign_id}',
        input: z.object({ campaign_id: id, name: z.string().min(1).max(200).optional(), starts_on: date.optional(), ends_on: date.optional(), ...campaignFields }),
    },
    {
        name: 'create_idea',
        title: 'Create an idea',
        description: 'A post idea (title and briefing, optionally a proposed date). A human or the studio turns it into a post.',
        operationId: 'createContentItem',
        scope: 'plan',
        method: 'POST',
        path: '/v1/content-items',
        input: z.object({ property_id: id, title: z.string().min(1).max(200), ...itemFields }),
        fixed: { status: 'idea' },
    },
    {
        name: 'create_draft',
        title: 'Create a draft post',
        description: 'A draft post with texts per language and formats. Add photos with set_variant_media afterwards. State only facts from verified knowledge items. A human reviews and approves it.',
        operationId: 'createContentItem',
        scope: 'plan',
        method: 'POST',
        path: '/v1/content-items',
        input: z.object({
            property_id: id,
            title: z.string().min(1).max(200),
            ...itemFields,
            localizations: z
                .array(z.object({ language: z.string().max(35), ...textFields }))
                .max(10)
                .optional()
                .describe('One text per language.'),
            formats: z.array(format).max(4).optional().describe('One variant per format.'),
        }),
        fixed: { status: 'draft' },
    },
    {
        name: 'update_content_item',
        title: 'Change a post',
        description: 'Title, briefing, pillar, campaign or proposed date of a post that is not published or archived. A change of the pillar alone keeps the version and an approval; every other change is a new version, and an approved post goes back to draft and needs approval again.',
        operationId: 'updateContentItem',
        scope: 'plan',
        method: 'PATCH',
        path: '/v1/content-items/{content_item_id}',
        input: z.object({ content_item_id: id, if_match: ifMatch, title: z.string().min(1).max(200).optional(), ...itemFields }),
    },
    {
        name: 'add_localization',
        title: 'Add a language',
        description: 'The text of a post in one more language; a new version.',
        operationId: 'createContentLocalization',
        scope: 'plan',
        method: 'POST',
        path: '/v1/content-items/{content_item_id}/localizations',
        input: z.object({ content_item_id: id, if_match: ifMatch, language: z.string().max(35), ...textFields }),
    },
    {
        name: 'update_localization',
        title: 'Change a text',
        description: 'Headline, caption, hashtags or call to action of one language of a post; a new version.',
        operationId: 'updateContentLocalization',
        scope: 'plan',
        method: 'PATCH',
        path: '/v1/content-items/{content_item_id}/localizations/{localization_id}',
        input: z.object({
            content_item_id: id,
            localization_id: id,
            if_match: ifMatch,
            headline: z.string().max(300).nullable().optional(),
            caption: z.string().max(10000).nullable().optional(),
            hashtags: z.array(z.string().max(100)).max(100).nullable().optional(),
            cta: z.string().max(300).nullable().optional(),
        }),
    },
    {
        name: 'set_variant_media',
        title: 'Choose photos',
        description: 'The ordered photos or video of one format of a post, from the media library (list_media). At most 10 on a carousel, 1 elsewhere.',
        operationId: 'replaceContentVariantMedia',
        scope: 'plan',
        method: 'PUT',
        path: '/v1/content-items/{content_item_id}/variants/{variant_id}/media',
        input: z.object({ content_item_id: id, variant_id: id, if_match: ifMatch, media: z.array(z.object({ asset_id: id })).max(10) }),
    },
    {
        name: 'add_variant',
        title: 'Add a format',
        description: 'One more format of a post, e.g. fb_feed next to ig_feed.',
        operationId: 'createContentVariant',
        scope: 'plan',
        method: 'POST',
        path: '/v1/content-items/{content_item_id}/variants',
        input: z.object({ content_item_id: id, if_match: ifMatch, format }),
    },
    {
        name: 'schedule_publication',
        title: 'Plan a post in the calendar',
        description: 'Puts one format and language of a post on an existing account at a time. Only an approved post is scheduled; otherwise it stays planned until a human approves it.',
        operationId: 'createPublication',
        scope: 'plan',
        method: 'POST',
        path: '/v1/publications',
        input: z.object({ variant_id: id, localization_id: id, social_account_id: id.nullable().optional(), ...time }),
    },
    {
        name: 'reschedule_publication',
        title: 'Move a post in the calendar',
        description: 'A new time for a planned or scheduled publication; nothing else changes.',
        operationId: 'updatePublication',
        scope: 'plan',
        method: 'PATCH',
        path: '/v1/publications/{publication_id}',
        input: z.object({ publication_id: id, if_match: ifMatch, ...time }),
    },
    {
        name: 'reschedule_publication_group',
        title: 'Move a post group',
        description: 'A new time for all publications of a group (e.g. Instagram and Facebook together).',
        operationId: 'updatePublicationGroup',
        scope: 'plan',
        method: 'PATCH',
        path: '/v1/publication-groups/{publication_group_id}',
        input: z.object({ publication_group_id: id, if_match: ifMatch, ...time }),
    },
    {
        name: 'add_comment',
        title: 'Comment on a post',
        description: 'A review note on a post; changes neither the post nor its approval.',
        operationId: 'addContentComment',
        scope: 'plan',
        method: 'POST',
        path: '/v1/content-items/{content_item_id}/comments',
        input: z.object({ content_item_id: id, body: z.string().min(1).max(5000) }),
    },
    {
        name: 'request_changes',
        title: 'Request changes',
        description: 'Sends a post in review back with a comment. There is no tool to approve.',
        operationId: 'requestContentChanges',
        scope: 'plan',
        method: 'POST',
        path: '/v1/content-items/{content_item_id}/request-changes',
        input: z.object({ content_item_id: id, if_match: ifMatch, comment: z.string().min(1).max(5000) }),
    },
];
/** Every tool, in the fixed order `tools/list` returns them (deterministic, spec 130 section 5.3). */
export const EXTERNAL_TOOLS = [...READ_TOOLS, ...PLAN_TOOLS, ...MEDIA_TOOLS];
export function externalToolNamed(name) {
    return EXTERNAL_TOOLS.find((tool) => tool.name === name);
}
/** The `{name}` placeholders of a tool path. */
export function pathArgumentsOf(tool) {
    return [...tool.path.matchAll(/\{([a-z_]+)\}/g)].map((match) => match[1]);
}
//# sourceMappingURL=tools.js.map